import { store } from "@/store/store"
import { CatalogManager } from "@/lib/catalog"
import {
	type AdjustmentHistoryTrackingProperties,
	updateCurrentAdjustmentHistoryIfNeeded,
} from "@/store/store-history"
import {
	AdjustmentHistoryInfo,
	HistoryRecordAction,
	PhotoKind,
	PhotoProcessingStatus,
	PhotoRecord,
} from "@/lib/catalog/types"
import { AdjustmentsResult } from "@/store/processing/image-processing"
import { resetBiasesInImageInfo, updateImageInfo } from "@/store/processing/image-info"
import { updatePhoto } from "@/store/photo/photos-state"
import { isCalibrationAtom, projectAtom } from "@/store/project/projects-state"
import { resizeAndStoreSmallPreview, updatePreviews } from "@/store/previews/previews-state"
import {
	ensureCullingClusterLeaderNotInvalid,
	ensurePhotoNotKeyNeighbor,
} from "@/lib/ai/ai-cluster"
import { lockedSinceSetup } from "../app-state"
import { PreviewInfo, PreviewSize } from "@/store/previews/previews-types"

type TrackedAdjustmentHistoryInfo = AdjustmentHistoryInfo & {
	trackingProperties?: AdjustmentHistoryTrackingProperties
}

export async function storeAdjustmentsHistoryAndUpdatePhoto(
	photo: PhotoRecord,
	results: AdjustmentsResult,
	info: TrackedAdjustmentHistoryInfo,
	shouldStorePhoto: boolean = true,
) {
	const { trackingProperties, ...historyInfo } = info
	const adjustmentOptions = { ...historyInfo, adjustments: results.adjustments }

	//Updates catalog records
	const updatedPhoto = await CatalogManager.saveAdjustmentHistory(photo, adjustmentOptions)

	//Updates in-memory history
	updateCurrentAdjustmentHistoryIfNeeded(photo, adjustmentOptions, trackingProperties)

	if (results.imageInfo) {
		updateImageInfo(results.imageInfo)
	}

	const isCalibration = store.get(isCalibrationAtom)

	let kind: PhotoKind = updatedPhoto.kind ?? photo.kind
	const prevKind = kind
	switch (historyInfo.action) {
		case HistoryRecordAction.ApplyPreset:
		case HistoryRecordAction.PasteAdjustments:
		case HistoryRecordAction.ManuallyAdjust:
		case HistoryRecordAction.ApplyColorMatch:
		case HistoryRecordAction.Reset:
			kind = isCalibration ? "default" : "locked"
			break
		case HistoryRecordAction.AutoEnhance:
			if (historyInfo.presetId != undefined) {
				kind = isCalibration ? "default" : "locked"
			}
			break
	}

	const finalPhoto = updatePhoto(
		photo.id,
		{
			...updatedPhoto,
			kind,
			status: "none" as PhotoProcessingStatus,
			error: null,
		},
		shouldStorePhoto,
	)
	const project = store.get(projectAtom)
	if (!project) return

	if (results.preview) {
		// Create an async function and execute it immediately to avoid blocking the main thread
		void (async () => {
			const previewBlob =
				results.preview instanceof Promise ? await results.preview : results.preview
			if (previewBlob) {
				const mediumPreviewInfo: PreviewInfo = {
					photoId: photo.id,
					data: previewBlob,
					size: PreviewSize.medium,
					projectId: project.id,
					digest: updatedPhoto.adjustmentsDigest ?? undefined,
					tags: ["edited", "rendered"],
				}

				void updatePreviews({ previewsInfo: [mediumPreviewInfo] })
				void resizeAndStoreSmallPreview(mediumPreviewInfo)
			}
		})()
	}

	if (results.originalPreview) {
		// Create an async function and execute it immediately to avoid blocking the main thread
		void (async () => {
			const previewBlob =
				results.originalPreview instanceof Promise
					? await results.originalPreview
					: results.originalPreview
			if (previewBlob) {
				const mediumPreviewInfo: PreviewInfo = {
					photoId: photo.id,
					data: previewBlob,
					size: PreviewSize.medium,
					projectId: project.id,
					tags: ["original", "rendered"],
				}

				void updatePreviews({ previewsInfo: [mediumPreviewInfo] })
			}
		})()
	}

	// ImageInfo update should be called after photo is updated
	if (prevKind != "locked" && kind == "locked") {
		ensurePhotoNotKeyNeighbor(photo.id)
		ensureCullingClusterLeaderNotInvalid(photo.id)
		store.set(lockedSinceSetup, store.get(lockedSinceSetup) + 1)
	}

	if (prevKind == "reference" && isCalibration) resetBiasesInImageInfo(photo.id)

	// Todo: trigger reprocesssing when a photo is automatically unmarked as reference
	// if (prevKind === "reference" && !store.get(isSettingUpStyleAtom)) {
	// 	const style = store.get(currentStyleAtom)
	// 	if (style) updateAIStyle(style)
	// }

	return finalPhoto
}
